import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import {
	CategoryDialog,
	type CategoryDialogMode,
} from "@/features/catalog/components/category-dialog";
import { MoveCategoryDialog } from "@/features/catalog/components/move-category-dialog";
import { RemoveTemplateDialog } from "@/features/catalog/components/remove-template-dialog";
import {
	type AdminCategoryRow,
	listAdminCategories,
	reorderCategories,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { hasPermission } from "@/lib/permissions";

/**
 * The category tree.
 *
 * A TREE rather than a table, because a category's meaning is its position.
 * "Wall Tiles" says nothing on its own; "Tiles > Wall Tiles" is the whole
 * definition, and a paginated list of 674 rows sorted by name cannot express it.
 *
 * WHAT THIS SCREEN IS ACTUALLY FOR, and the obstacle it had to clear. Plan 003
 * grows the taxonomy to roughly 40 top-level and 330 second-level nodes. Every
 * category that existed when this was built was top-level, childless, and
 * carried a specification template -- and templates live only on leaves, so
 * `assertParentCanTakeAChild` refused a child under all five. The refusal
 * advised moving or deleting the template, and the API offered neither, so the
 * taxonomy could not be grown at all. `DELETE .../template` is the way through,
 * and this screen is where an operator walks the sequence: retire the template,
 * add the children, give each new leaf its own template, then re-file the
 * products with the bulk category move.
 */

/** A node plus the children resolved under it. */
interface TreeNode extends AdminCategoryRow {
	children: TreeNode[];
}

/**
 * Nest the flat response.
 *
 * A row whose `parentId` names a category that is not in the response is
 * treated as a ROOT rather than dropped -- showing a subtree in the wrong place
 * beats making it vanish with nothing on screen to say so.
 *
 * An earlier version of this comment called that case impossible "because the
 * endpoint is unpaginated". It is not unpaginated: `listCategoriesForAdmin`
 * caps at a thousand rows, and `total` used to be the length of what came back,
 * so truncation was invisible. `total` is a real count now and the screen warns
 * when `items.length` falls short of it -- because silently re-parenting half a
 * taxonomy to the top is a worse failure than either.
 */
export function buildTree(rows: AdminCategoryRow[]): TreeNode[] {
	const byId = new Map<string, TreeNode>(
		rows.map((row) => [row.id, { ...row, children: [] }]),
	);
	const roots: TreeNode[] = [];

	for (const row of rows) {
		const node = byId.get(row.id);
		if (!node) continue;
		const parent = row.parentId ? byId.get(row.parentId) : undefined;
		if (parent) parent.children.push(node);
		else roots.push(node);
	}

	return roots;
}

/** Ids of every node matching the query, plus every ancestor of one. */
function matchingIds(
	nodes: TreeNode[],
	query: string,
	into: Set<string> = new Set(),
): Set<string> {
	const needle = query.trim().toLowerCase();
	if (needle === "") return into;

	/*
	 * A node is kept when it matches OR when anything beneath it does, and the
	 * recursion is what carries ancestors along: a parent whose `below` is true
	 * adds itself, so the whole path from a hit up to its root ends up in the
	 * set without threading one through.
	 *
	 * `.map(...).some(...)` rather than `.some(...)` directly, because `some`
	 * short-circuits: the first matching child would stop the walk, and every
	 * sibling after it would be left out of the set even when it matches. Pinned
	 * by the two-matching-siblings case in the tests, since the difference is
	 * invisible in any fixture where a parent has one child.
	 */
	const walk = (node: TreeNode): boolean => {
		const self =
			node.name.toLowerCase().includes(needle) ||
			node.slug.toLowerCase().includes(needle);
		const below = node.children.map(walk).some(Boolean);
		if (self || below) {
			into.add(node.id);
			return true;
		}
		return false;
	};

	for (const node of nodes) walk(node);
	return into;
}

export function CatalogCategoriesPage() {
	const queryClient = useQueryClient();
	const { operator } = useAdminAuthStore();
	const canEdit = hasPermission(operator, "admin:catalog:taxonomy");

	const [query, setQuery] = useState("");
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
	const [dialog, setDialog] = useState<CategoryDialogMode | null>(null);
	const [moving, setMoving] = useState<AdminCategoryRow | null>(null);
	/*
	 * WHICH BUTTON TO PUT FOCUS BACK ON once the tree has re-rendered.
	 *
	 * The row moves out from under the pointer, so a keyboard operator who has
	 * just pressed Up lands on whatever now occupies that position -- or on
	 * nothing, if the button they used is now disabled because the row reached
	 * the end of the group. That operator is the reason this unit exists rather
	 * than drag-and-drop, so losing their place defeats it.
	 */
	const refocus = useRef<string | null>(null);
	const [removingTemplate, setRemovingTemplate] =
		useState<AdminCategoryRow | null>(null);

	const categoriesQuery = useQuery({
		queryKey: ["admin-catalog-categories"],
		queryFn: listAdminCategories,
	});

	const rows = categoriesQuery.data?.items ?? [];
	/*
	 * The cap, made visible.
	 *
	 * `listCategoriesForAdmin` stops at a thousand rows. Silently, until now:
	 * `total` was the length of what came back, so a truncated response looked
	 * complete -- and `buildTree` promotes any row whose parent is missing to a
	 * root, so the screen would have shown a mangled taxonomy with every sign of
	 * being the real one.
	 */
	const truncated = rows.length < (categoriesQuery.data?.total ?? 0);
	/*
	 * Memoised because both walk the WHOLE taxonomy and both would otherwise
	 * re-run on every keystroke and every unrelated state change -- up to a
	 * thousand nodes each, twice per render.
	 */
	const tree = useMemo(() => buildTree(rows), [rows]);
	const matches = useMemo(() => matchingIds(tree, query), [tree, query]);
	const searching = query.trim() !== "";

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ["admin-catalog-categories"] });

	function toggle(id: string): void {
		setExpanded((current) => {
			const next = new Set(current);
			if (!next.delete(id)) next.add(id);
			return next;
		});
	}

	/*
	 * THE WHOLE SIBLING GROUP, in the order it is rendered in.
	 *
	 * The endpoint renumbers every child of the parent rather than swapping two,
	 * because `displayOrder` defaults to 0 and almost every group is uniformly
	 * zero -- so a swap writes 0 over 0 and changes nothing. Sending the rendered
	 * order is what makes the first reorder of any group durable.
	 */
	const [reorderError, setReorderError] = useState<string | null>(null);

	const reorder = useMutation({
		mutationFn: ({
			parentId,
			orderedIds,
		}: {
			parentId: string | null;
			orderedIds: string[];
		}) => reorderCategories(parentId, orderedIds),
		onMutate: () => setReorderError(null),
		onSuccess: async () => {
			setReorderError(null);
			await invalidate();
		},
		/*
		 * NOTHING IS PATCHED LOCALLY, so a refused reorder leaves the rendered
		 * order exactly as it was. The alternative -- moving the row optimistically
		 * and rolling back -- shows an operator a move that did not persist, which
		 * is the one outcome worse than a slow one.
		 *
		 * WHICH IS WHY THE REFUSAL HAS TO BE RENDERED. With nothing patched and
		 * nothing reported, a refused reorder was a button that did nothing: the
		 * row stayed put, no message appeared, and the operator pressed it again.
		 * `reorderSiblings` writes two sentences for exactly this moment -- "a
		 * category in that order no longer belongs to this parent, reload the tree
		 * and try again" is the answer to a tree another operator has changed
		 * underneath this one -- and neither could reach the screen.
		 */
		onError: (error: unknown) =>
			setReorderError(
				apiErrorMessage(error, "Could not reorder these categories."),
			),
	});

	/*
	 * Focus follows the moved row once the refetch has landed.
	 *
	 * Keyed off `categoriesQuery.data` rather than the mutation's success: the
	 * tree re-renders when the new data arrives, and focusing before that would
	 * address a button React is about to replace.
	 */
	// The dependency is not read in the body -- it is the SIGNAL. The tree
	// re-renders when new data arrives, and focusing before that would address a
	// button React is about to replace.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	useEffect(() => {
		const label = refocus.current;
		if (!label) return;
		refocus.current = null;

		const button = document.querySelector<HTMLButtonElement>(
			`button[aria-label="${label}"]`,
		);
		// Disabled when the row reached the end of its group, in which case the
		// opposite button is where the operator can still act.
		if (button && !button.disabled) button.focus();
	}, [categoriesQuery.data]);

	function moveSibling(
		siblings: TreeNode[],
		index: number,
		direction: -1 | 1,
	): void {
		const target = index + direction;
		if (target < 0 || target >= siblings.length) return;

		const ordered = siblings.map((one) => one.id);
		[ordered[index], ordered[target]] = [ordered[target], ordered[index]];

		const parentId = siblings[0]?.parentId ?? null;
		/*
		 * Remembered BEFORE the write, keyed by the moved category and the
		 * direction it went. After the refetch the row is at a new index, so the
		 * button to return to is found by label rather than by position.
		 */
		refocus.current = `Move ${siblings[index].name} ${
			direction === -1 ? "up" : "down"
		}`;
		reorder.mutate({ parentId, orderedIds: ordered });
	}

	function renderNode(
		node: TreeNode,
		depth: number,
		siblings: TreeNode[],
		index: number,
	) {
		// While searching, the matched paths are open regardless of what the
		// operator had collapsed -- a search that returns a hit inside a closed
		// branch and shows nothing is a search that looks broken.
		const isOpen = searching ? matches.has(node.id) : expanded.has(node.id);
		const hidden = searching && !matches.has(node.id);
		if (hidden) return null;

		return (
			<li key={node.id}>
				<div
					className="flex flex-wrap items-center gap-2 border-b py-2"
					style={{ paddingLeft: `${depth * 1.25}rem` }}
				>
					{node.children.length > 0 && !searching ? (
						/*
						 * Hidden WHILE SEARCHING, not merely disabled.
						 *
						 * A search decides what is open -- matched paths are forced
						 * open so a hit inside a collapsed branch is reachable -- and
						 * `toggle` writes only the non-search `expanded` set. The
						 * button therefore rendered during a search, showed a state,
						 * and did nothing when pressed.
						 */
						<Button
							variant="ghost"
							size="sm"
							onClick={() => toggle(node.id)}
							aria-expanded={isOpen}
							aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.name}`}
						>
							{isOpen ? "-" : "+"}
						</Button>
					) : (
						// A spacer, so names line up whether or not a node has children.
						<span className="inline-block w-9" aria-hidden="true" />
					)}

					<span className="font-medium">{node.name}</span>
					<span className="text-xs text-muted-foreground">{node.slug}</span>

					<span className="text-xs text-muted-foreground">
						{node.productCount} product{node.productCount === 1 ? "" : "s"}
					</span>

					{!node.isActive ? <Badge variant="warning">Inactive</Badge> : null}
					{node.hasTemplate ? (
						<Badge variant="default">Spec template</Badge>
					) : null}

					{/*
					 * A FACT, deliberately not an alarm.
					 *
					 * A category with children can hold no template, so its products
					 * publish without any required-attribute check. That state is
					 * sometimes exactly right -- the labour taxonomy files trade day
					 * rates this way on purpose, and eight of the nine seeded day-rate
					 * categories match this predicate -- and sometimes it is a template
					 * removal nobody finished. `AdminCategoryRow` carries nothing that
					 * separates the two, so this states the condition and leaves the
					 * judgement to the operator.
					 *
					 * An amber warning here was wrong on all eight rows anyone would
					 * actually see, which is how a signal becomes noise. It is also why
					 * this is not a block: a previous version of this branch made the
					 * publish gate refuse the state outright, froze those eight
					 * products, and had to be reverted.
					 *
					 * Costs nothing: all three fields are already on the row.
					 */}
					{node.children.length > 0 &&
					node.productCount > 0 &&
					!node.hasTemplate ? (
						<Badge
							variant="default"
							title="This category has subcategories, so it cannot carry a specification template and its products publish without a required-attribute check. Intended for things like trade day rates; unintended if a template was removed and never replaced."
						>
							No spec check
						</Badge>
					) : null}

					{canEdit ? (
						<span className="ml-auto flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setDialog({ kind: "edit", category: node })}
							>
								Edit
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setMoving(node)}
							>
								Move
							</Button>
							{/*
							 * HIDDEN WHILE SEARCHING, not disabled.
							 *
							 * The search narrows the rendered rows, so "the sibling above"
							 * on screen is not the sibling above in the group -- and the
							 * endpoint renumbers the WHOLE group, so an order computed from
							 * a filtered list would reorder against rows the operator
							 * cannot see. The expand toggle two blocks up is hidden during
							 * a search for the same class of reason.
							 */}
							{!searching && siblings.length > 1 ? (
								<>
									<Button
										variant="ghost"
										size="sm"
										disabled={index === 0 || reorder.isPending}
										aria-label={`Move ${node.name} up`}
										onClick={() => moveSibling(siblings, index, -1)}
									>
										Up
									</Button>
									<Button
										variant="ghost"
										size="sm"
										disabled={
											index === siblings.length - 1 || reorder.isPending
										}
										aria-label={`Move ${node.name} down`}
										onClick={() => moveSibling(siblings, index, 1)}
									>
										Down
									</Button>
								</>
							) : null}
							{/*
							 * Only on LEAVES, because `upsertSpecTemplate` refuses any other
							 * category: templates live only on leaves. Offering the link on a
							 * parent would open a screen whose save can never succeed.
							 *
							 * The wording follows the row: a category with no template is
							 * having one created, which is the common case in a taxonomy
							 * where most nodes carry none.
							 */}
							{node.children.length === 0 ? (
								<Button asChild variant="outline" size="sm">
									<Link to={`/catalog/categories/${node.id}/template`}>
										{node.hasTemplate ? "Edit specs" : "Add specs"}
									</Link>
								</Button>
							) : null}
							<Button
								variant="outline"
								size="sm"
								disabled={node.hasTemplate}
								onClick={() => setDialog({ kind: "create", parent: node })}
							>
								Add child
							</Button>
							{node.hasTemplate ? (
								<>
									{/*
									 * The reason the control above is disabled, next to the
									 * control. A disabled button with no explanation is how an
									 * operator concludes the screen is broken -- and this rule
									 * is not guessable: nothing about "add a category"
									 * suggests specification templates.
									 */}
									<span className="text-xs text-muted-foreground">
										Carries a spec template, so it cannot take children yet.
									</span>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setRemovingTemplate(node)}
									>
										Remove template
									</Button>
								</>
							) : null}
						</span>
					) : null}
				</div>

				{node.children.length > 0 && isOpen ? (
					<ul>
						{node.children.map((child, childIndex) =>
							renderNode(child, depth + 1, node.children, childIndex),
						)}
					</ul>
				) : null}
			</li>
		);
	}

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader className="flex flex-row items-start justify-between gap-4">
					<div>
						<CardTitle>Categories</CardTitle>
						<CardDescription>
							The taxonomy products are filed under. A category's position is
							its meaning, so this is a tree rather than a list.
						</CardDescription>
					</div>
					<div className="flex items-center gap-2">
						{/*
						 * A REFRESH, because this screen disables controls from data it
						 * has no reason to refetch.
						 *
						 * Add-child is disabled from `hasTemplate`, which is whatever
						 * age the tab is. Once someone else removes that template there
						 * was no way back to a working button short of a page reload --
						 * a screen telling an operator they cannot do something they
						 * now can, with no way to find out.
						 */}
						<Button
							variant="outline"
							onClick={() => invalidate()}
							disabled={categoriesQuery.isFetching}
						>
							{categoriesQuery.isFetching ? "Refreshing..." : "Refresh"}
						</Button>
						{canEdit ? (
							<Button
								onClick={() => setDialog({ kind: "create", parent: null })}
							>
								New top-level category
							</Button>
						) : null}
					</div>
				</CardHeader>
				<CardContent className="grid gap-3">
					<Input
						placeholder="Search by name or slug"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>

					{truncated ? (
						<p
							role="alert"
							className="rounded-2xl border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900"
						>
							Showing {rows.length} of {categoriesQuery.data?.total} categories.
							The rest are not on this screen, and any whose parent is missing
							appear at the top level rather than where they belong. The search
							filters only what is already loaded, so it cannot reach them --
							editing the tree from here is unsafe until the taxonomy fits under
							the cap or this endpoint is paginated by level.
						</p>
					) : null}

					{reorderError ? (
						/*
						 * Above the tree rather than beside a row: the refusal is about
						 * the GROUP, and the row that was clicked has not moved -- so
						 * anchoring it to that row would point at the thing that did not
						 * change.
						 */
						<p role="alert" className="text-sm text-red-700">
							{reorderError}
						</p>
					) : null}

					{categoriesQuery.isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
						</div>
					) : categoriesQuery.isError ? (
						<p role="alert" className="text-sm text-red-700">
							{apiErrorMessage(
								categoriesQuery.error,
								"Could not load the categories.",
							)}
						</p>
					) : tree.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No categories yet. The first one is a top-level category.
						</p>
					) : searching && matches.size === 0 ? (
						<p className="text-sm text-muted-foreground">
							Nothing matches "{query}".
						</p>
					) : (
						/*
						 * NESTED LISTS, not `role="tree"`.
						 *
						 * The ARIA tree pattern comes with obligations this component does
						 * not meet -- arrow-key navigation between nodes, a roving tabindex,
						 * home/end -- and a role that announces behaviour the widget does not
						 * have is worse for a screen-reader user than no role: it changes how
						 * the whole subtree is presented and then does not respond to the keys
						 * that presentation promises.
						 *
						 * Nested `ul`/`li` conveys the same hierarchy honestly, and the
						 * expand control is a real button carrying `aria-expanded`. The tree
						 * role becomes right when the keyboard handling is written, not
						 * before.
						 */
						<ul>
							{tree.map((node, index) => renderNode(node, 0, tree, index))}
						</ul>
					)}
				</CardContent>
			</Card>

			{dialog ? (
				/*
				 * KEYED BY ITS TARGET, so switching nodes remounts it.
				 *
				 * `CategoryDialog` seeds its draft from `mode` once, on mount. Without
				 * a key, changing `mode` without an intervening `null` reuses the
				 * instance and keeps the previous node's draft -- and neither overlay
				 * traps focus, so tabbing to a background Edit button and pressing
				 * Enter reaches exactly that. The result is one category's edits saved
				 * onto another, which is data corruption rather than a display glitch.
				 */
				<CategoryDialog
					key={
						dialog.kind === "edit"
							? `edit-${dialog.category.id}`
							: `create-${dialog.parent?.id ?? "root"}`
					}
					mode={dialog}
					onClose={() => setDialog(null)}
					onSaved={invalidate}
				/>
			) : null}

			{moving ? (
				<MoveCategoryDialog
					// Keyed so retargeting -- move one, close, move another -- cannot
					// carry the previous category's chosen destination or error banner.
					key={moving.id}
					category={moving}
					onClose={() => setMoving(null)}
					onMoved={invalidate}
				/>
			) : null}

			{removingTemplate ? (
				/*
				 * KEYED TOO, for the same reason `CategoryDialog` above is.
				 *
				 * Extracting this dialog stopped its mutation outliving the PAGE,
				 * which is what made a failed removal show its error over the next
				 * category opened. It did not stop the mutation outliving a
				 * RETARGET: without a key, moving from one category to another
				 * without closing reuses the instance, and with it the previous
				 * category's error banner. The commit that extracted it called that
				 * fixed structurally, which was half true -- two reviewers found the
				 * half that was not.
				 */
				<RemoveTemplateDialog
					key={removingTemplate.id}
					category={removingTemplate}
					onClose={() => setRemovingTemplate(null)}
					onRemoved={invalidate}
				/>
			) : null}
		</div>
	);
}
